import { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector, shallowEqual } from "react-redux"
import Button from "react-bootstrap/Button"
import Table from "react-bootstrap/Table"
import "./index.scss"

import { IoIosArrowUp, IoIosArrowDown } from "react-icons/io"

import { Text } from "components/Form"
import ESignatureModal from "components/Lims/ESignatureModal"
import { limsSstSpec, limsSstResult, limsSstTemplates, message } from "state_management"
import { computeRsd, judgeSst, buildSstRecord } from "state_management/lims/limsSstResult"
import { selectTemplate } from "state_management/lims/limsSstTemplates"
import { canSign } from "state_management/lims/segregationOfDuties"
import useT from "utils/hooks/useT"

// SstPanel (PRD-005 Area 3 / SPEC-017; F4 / SPEC-036) — the per-run System Suitability Test on a
// PLANNED analysis task. Shown ABOVE ResultEntryPanel when the analysis definition requires an SST
// (selectRequiresSst). The analyst enters the (typically six) replicate injections' AREA and
// RETENTION TIME; the calculation engine derives RSD-area and RSD-RT LIVE (via calcEngine.rsd,
// reused through limsSstResult.computeRsd). Tailing factor, plate count (N) and resolution (Rs) are
// ENTERED from the instrument report and judged against the SST criteria. Each parameter shows
// Passed/Failed, the overall SST status is derived, and the analyst e-signs the SST (their own
// sign-off).
//
// F4 (SPEC-036): when the definition tags an instrumentType (HPLC/GC), the SST runs against a
// SHARED SST TEMPLATE from the library — the template's criteria drive the judgement (and its id is
// stored on the result). With no template the definition's INLINE criteria apply (the
// back-compatible fallback). #4174: the template does not appear on this panel AT ALL any more —
// no picker, no label, no name. It is the DEFINITION's business (Setup › System Suitability), which
// is the only place the choice can be made under change control; picking it per run let the analyst
// choose the acceptance criteria at the moment of measuring, the same objection R24 (#4075) raised
// against the inline-criteria option. The panel resolves the template silently, to judge against and
// to stamp on the saved result, and shows the analyst the CRITERIA instead. The SST is
// also a 2-step
// signature object: after the analyst's technical sign, a SUPERVISOR signs it off (cap supervisor,
// four-eyes ≠ analyst); the send-to-review gate needs BOTH when the definition requires an SST.
//
// With showLims off this never mounts (the parent gates on showLims + selectRequiresSst). The
// component owns only the editable draft; the persisted/signed record lives in redux.
export default function SstPanel({ analysisId, analysisDefinitionId }) {
  const { t } = useT("common")
  const dispatch = useDispatch()

  const spec = useSelector(
    (state) => (analysisDefinitionId ? limsSstSpec.selectSstSpec(state, analysisDefinitionId) : undefined),
    shallowEqual
  )
  const saved = useSelector((state) => limsSstResult.selectSstResult(state, analysisId), shallowEqual)

  const injectionCount = (spec && spec.injectionCount) || 6
  const signed = !!(saved && saved.signature)
  const supervisorSigned = !!(saved && saved.supervisorSignature)
  // F4 — the supervisor step applies whenever the definition requires an SST. The SST is fully
  // signed only once BOTH the analyst and the supervisor have signed; the panel drives off the
  // two individual flags below (the send-to-review gate enforces "both" via isSstPassedAndSigned).
  const requiresSupervisor = !!(spec && spec.requiresSst)

  // #4129 — four-eyes for the SST supervisor step, mirroring the backend's exclusion set exactly
  // (`sst_results._supervisor_sign`): the member who ENTERED the SST and the member who
  // ANALYST-SIGNED it may not also supply the supervisor signature, so one person can never
  // provide both SST signatures.
  const supervisorExcludedMemberIds = useMemo(() => {
    const excluded = new Set()
    if (saved && saved.enteredBy) excluded.add(saved.enteredBy)
    if (saved && saved.signature && saved.signature.memberId) excluded.add(saved.signature.memberId)
    return Array.from(excluded)
  }, [saved])
  // #4129 — may the CURRENT user apply the supervisor sign-off here? (holds "supervisor" AND is
  // not the enterer / analyst signer). This is the FAIL-FAST half that ghosts the button below;
  // the backend re-verifies capability + four-eyes on PATCH /sstResults/<id> and stays the law.
  const supervisorGate = useSelector(
    (state) => canSign(state, { capability: "supervisor", excludedMemberIds: supervisorExcludedMemberIds }),
    shallowEqual
  )
  // #4142 (SPEC-059) — entering an SST is the ANALYST's work: it is their self-attestation, and the
  // enterer is what the supervisor four-eyes exclusion above is computed from. Without the
  // capability every entry control is ghosted, so the panel stops offering work that cannot be
  // signed or sent on. `canSign` is reused rather than a bare capability read so the dev
  // GMP-relax flag keeps behaving the same here as on every other LIMS gate.
  const analystGate = useSelector((state) => canSign(state, { capability: "analyst" }), shallowEqual)
  const canEnterSst = analystGate.allowed

  // Editable draft: the injection rows + the three entered instrument parameters + the chosen
  // template. Seeded from the saved record when present, else from a blank set of `injectionCount`
  // rows and the definition's default template.
  const [injections, setInjections] = useState([])
  const [tailing, setTailing] = useState("")
  const [plateCount, setPlateCount] = useState("")
  const [resolution, setResolution] = useState("")
  const [showSign, setShowSign] = useState(false)
  const [showSupervisorSign, setShowSupervisorSign] = useState(false)
  const [saving, setSaving] = useState(false)
  const [seeded, setSeeded] = useState(false)

  // The three reasons an entry control is dead, in one place: already signed (write-once), a save
  // in flight, or the viewer is not an analyst. Declared AFTER `saving`: it reads that state, and
  // above the useState it was a temporal-dead-zone reference that threw on every render of this
  // panel -- caught in the browser, not by lint or the build, because TDZ is a runtime error.
  const entryDisabled = signed || saving || !canEnterSst

  // F4 — load the shared template library once the panel mounts (cheap, team-scoped; the slice
  // dedupes). Needed to populate the picker + resolve the effective criteria.
  useEffect(() => {
    dispatch(limsSstTemplates.fetch())
  }, [dispatch])

  // Seed the draft ONCE from the saved record (or blank rows). Guarded so live edits aren't clobbered
  // by later store updates (e.g. our own save round-trip).
  useEffect(() => {
    if (seeded) return
    if (saved) {
      const draft = draftFromSaved(saved)
      setInjections(draft.injections)
      setTailing(draft.tailing)
      setPlateCount(draft.plateCount)
      setResolution(draft.resolution)
      setSeeded(true)
    } else if (injections.length === 0) {
      setInjections(Array.from({ length: injectionCount }, () => ({ area: "", rt: "" })))
    }
  }, [saved, injectionCount, seeded, injections.length, spec])

  const setInjAt = (idx, key, v) =>
    setInjections((prev) => prev.map((row, i) => (i === idx ? { ...row, [key]: v } : row)))

  const selectedTemplateId = resolveTemplateId(saved, spec)

  // F4 — the picked template record (any version, resolved by id), and the EFFECTIVE criteria the
  // judgement uses: the template's criteria when one is resolved, else the definition's inline spec.
  // Both shapes share the same field names (rsdAreaMax / tailingMin / … ) so judgeSst is reused.
  const pickedTemplate = useSelector((state) => selectTemplate(state, selectedTemplateId), shallowEqual)
  const effectiveSpec = useMemo(() => {
    if (selectedTemplateId && pickedTemplate) {
      return {
        rsdAreaMax: pickedTemplate.rsdAreaMax,
        rsdRtMax: pickedTemplate.rsdRtMax,
        tailingMin: pickedTemplate.tailingMin,
        tailingMax: pickedTemplate.tailingMax,
        plateCountMin: pickedTemplate.plateCountMin,
        resolutionMin: pickedTemplate.resolutionMin,
      }
    }
    return spec
  }, [selectedTemplateId, pickedTemplate, spec])

  // Live computed RSDs + per-parameter judgement from the current draft (vs the effective criteria).
  const live = useMemo(() => {
    const rsdArea = computeRsd(injections, "area")
    const rsdRt = computeRsd(injections, "rt")
    const record = { computed: { rsdArea, rsdRt }, tailing, plateCount, resolution }
    const { perParam, overall } = judgeSst(record, effectiveSpec)
    return { rsdArea, rsdRt, perParam, overall }
  }, [injections, tailing, plateCount, resolution, effectiveSpec])

  const buildDraftRecord = () =>
    buildSstRecord({
      analysisId,
      analysisDefinitionId,
      entry: { injections, tailing, plateCount, resolution },
      spec: effectiveSpec,
      // F4 — the picked shared template id (server denormalizes its current version on the result).
      selectedSstTemplateId: selectedTemplateId || null,
    })

  // Save the SST draft (server recomputes + judges), then sign it with the analyst's e-signature
  // (password verified server-side; "analyst" capability). Saving first guarantees the signed
  // record carries the latest entered values + judgement. Returns the promise so the e-signature
  // dialog can stay open and surface a backend 403 (wrong password / missing capability) inline.
  const handleSignSubmit = (signature) => {
    setSaving(true)
    return dispatch(limsSstResult.saveSst(buildDraftRecord()))
      .then((savedRec) => dispatch(limsSstResult.signSst(savedRec.id, signature)))
      .then(() => {
        setShowSign(false)
        dispatch(message.success(t("limsSstSignedMsg")))
      })
      .finally(() => setSaving(false))
  }

  // F4 — the supervisor signs the technically-signed SST (cap "supervisor", four-eyes ≠ analyst).
  // No save first (the record is frozen after the technical sign); a 403 stays inline in the dialog.
  const handleSupervisorSignSubmit = (signature) => {
    setSaving(true)
    return dispatch(limsSstResult.signSstSupervisor(analysisId, signature))
      .then(() => {
        setShowSupervisorSign(false)
        dispatch(message.success(t("limsSstSupervisorSignedMsg")))
      })
      .finally(() => setSaving(false))
  }

  // Save the SST without signing (lets the analyst record progress / re-judge). Re-seed guard stays
  // set so the save round-trip doesn't reset the in-progress edits.
  const handleSave = () => {
    setSaving(true)
    setSeeded(true)
    dispatch(limsSstResult.saveSst(buildDraftRecord()))
      .then(() => dispatch(message.success(t("limsSstSaved"))))
      .catch(() => dispatch(message.error(t("limsSstSaveFailed"))))
      .finally(() => setSaving(false))
  }

  const fmt = (v) => (v === null || v === undefined || !Number.isFinite(v) ? t("limsSstNotEntered") : v.toFixed(2))

  const supervisorGhostHint = supervisorGhostHintFor(supervisorGate, t)

  // #4175 — `collapseOverride` is null until the viewer clicks, so the panel follows the SST's own
  // state until they say otherwise (see isCollapsed).
  const [collapseOverride, setCollapseOverride] = useState(null)
  const collapsed = isCollapsed(collapseOverride, { signed, requiresSupervisor, supervisorSigned })

  // #4175 — collapsed is the whole panel minus its body. Returning early rather than wrapping the
  // body in a conditional keeps every control at the nesting it already had.
  if (collapsed) {
    return (
      <div className="SstPanel">
        <SstPanelHeader collapsed onToggle={() => setCollapseOverride(false)} t={t} />
      </div>
    )
  }

  return (
    <div className="SstPanel">
      <SstPanelHeader collapsed={false} onToggle={() => setCollapseOverride(true)} t={t} />


      {/* #4142: say WHY the panel is read-only, rather than letting it look broken — the same
          treatment the result grid got in #4111. */}
      {!canEnterSst && <p className="SstPanel-message">{t("limsSstNeedsAnalystCapability")}</p>}

      <Table responsive className="SstPanel-injections">
        <thead>
          <tr>
            <th>{t("limsSstInjection")}</th>
            <th>{t("limsSstArea")}</th>
            <th>{t("limsSstRt")}</th>
          </tr>
        </thead>
        <tbody>
          {injections.map((row, idx) => (
            <tr key={idx}>
              <td>{idx + 1}</td>
              <td>
                <Text value={row.area} onChange={(v) => setInjAt(idx, "area", v)} disabled={entryDisabled} />
              </td>
              <td>
                <Text value={row.rt} onChange={(v) => setInjAt(idx, "rt", v)} disabled={entryDisabled} />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <Table responsive className="SstPanel-params">
        <thead>
          <tr>
            <th>{t("limsSstParameter")}</th>
            <th>{t("limsSstValue")}</th>
            <th>{t("limsSstCriterion")}</th>
            <th>{t("limsSstResult")}</th>
          </tr>
        </thead>
        <tbody>
          <ParamRow
            label={t("limsSstComputedRsdArea")}
            value={fmt(live.rsdArea)}
            criterion={maxCriterion(effectiveSpec && effectiveSpec.rsdAreaMax)}
            pass={live.perParam.rsdArea}
            t={t}
          />
          <ParamRow
            label={t("limsSstComputedRsdRt")}
            value={fmt(live.rsdRt)}
            criterion={maxCriterion(effectiveSpec && effectiveSpec.rsdRtMax)}
            pass={live.perParam.rsdRt}
            t={t}
          />
          <tr>
            <td>{t("limsSstTailing")}</td>
            <td>
              <Text value={tailing} onChange={setTailing} disabled={entryDisabled} />
            </td>
            <td>{rangeCriterion(effectiveSpec && effectiveSpec.tailingMin, effectiveSpec && effectiveSpec.tailingMax)}</td>
            <td><ResultFlag pass={live.perParam.tailing} t={t} /></td>
          </tr>
          <tr>
            <td>{t("limsSstPlateCount")}</td>
            <td>
              <Text value={plateCount} onChange={setPlateCount} disabled={entryDisabled} />
            </td>
            <td>{minCriterion(effectiveSpec && effectiveSpec.plateCountMin)}</td>
            <td><ResultFlag pass={live.perParam.plateCount} t={t} /></td>
          </tr>
          <tr>
            <td>{t("limsSstResolution")}</td>
            <td>
              <Text value={resolution} onChange={setResolution} disabled={entryDisabled} />
            </td>
            <td>{minCriterion(effectiveSpec && effectiveSpec.resolutionMin)}</td>
            <td><ResultFlag pass={live.perParam.resolution} t={t} /></td>
          </tr>
        </tbody>
      </Table>

      <div className="SstPanel-overall">
        <span className="SstPanel-overall-label">{t("limsSstOverall")}:</span>{" "}
        <ResultFlag pass={live.overall === null ? null : live.overall === "PASS"} t={t} />
        {signed && saved.signature && (
          <span className="SstPanel-signed">
            {" "}({t("limsSstSignedBy", { initials: saved.signature.initials || "" })})
          </span>
        )}
        {supervisorSigned && saved.supervisorSignature && (
          <span className="SstPanel-signed SstPanel-signed--supervisor">
            {" "}({t("limsSstSupervisorSignedBy", { initials: saved.supervisorSignature.initials || "" })})
          </span>
        )}
      </div>

      {!signed && (
        <div className="SstPanel-actions">
          <Button variant="light" onClick={handleSave} disabled={entryDisabled} title={canEnterSst ? undefined : t("limsSstNeedsAnalystCapability")}>
            {t("save")}
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              if (live.overall !== "PASS") {
                dispatch(message.warning(t("limsSstSignBlockedNotPassed")))
                return
              }
              setShowSign(true)
            }}
            disabled={saving || !canEnterSst}
            title={canEnterSst ? undefined : t("limsSstNeedsAnalystCapability")}
          >
            {t("limsSstSign")}
          </Button>
        </div>
      )}

      {/* F4 — after the analyst's technical sign, the supervisor signs off (only when the
          definition requires an SST, and not already supervisor-signed).
          #4129: the row renders for EVERYONE but the button is GHOSTED for a viewer who may not
          supervise (missing capability, or four-eyes because they entered / analyst-signed it),
          with a hint saying WHY — GMP/ALCOA+ "all can SEE, only the qualified can ACT"
          (K16/SPEC-022; SPEC-027 rejects hiding precisely because it explains nothing). It used
          to render fully enabled for everyone, so the block only surfaced as a raw backend 403
          AFTER the signer had typed their password. */}
      {signed && requiresSupervisor && !supervisorSigned && (
        <>
          <div className="SstPanel-actions SstPanel-actions--supervisor">
            <span className="SstPanel-supervisor-hint">{t("limsSstSupervisorPending")}</span>
            <Button
              variant="primary"
              onClick={() => setShowSupervisorSign(true)}
              disabled={saving || !supervisorGate.allowed}
              title={supervisorGhostHint || undefined}
            >
              {t("limsSstSupervisorSign")}
            </Button>
          </div>
          {supervisorGhostHint && <p className="SstPanel-supervisor-ghost">{supervisorGhostHint}</p>}
        </>
      )}

      {showSign && (
        <ESignatureModal
          title={t("limsSstPanelTitle")}
          /* #4129 — the technical sign needs the "analyst" capability (no four-eyes). Passing it
             lets the dialog state that up front instead of accepting a password and 403-ing. */
          requiredCapability="analyst"
          onSign={handleSignSubmit}
          onCancel={() => setShowSign(false)}
        />
      )}

      {showSupervisorSign && (
        <ESignatureModal
          title={t("limsSstSupervisorSign")}
          /* #4129 — same gate as the ghosted button, so the dialog is a dead end for a signer who
             may not supervise (it renders the blocked panel instead of the password form). */
          requiredCapability="supervisor"
          excludedMemberIds={supervisorExcludedMemberIds}
          onSign={handleSupervisorSignSubmit}
          onCancel={() => setShowSupervisorSign(false)}
        />
      )}
    </div>
  )
}

// #4129 — one-line explanation for a viewer whose supervisor sign-off is ghosted: four-eyes (they
// entered or analyst-signed this SST) vs a missing supervisor capability. Null when they may sign.
// Mirrors ResultEntryPanel's reviewer/supervisor ghost hints.
function supervisorGhostHintFor(gate, t) {
  if (gate.allowed) {
    return null
  }
  return gate.reasonKey === "limsEsigBlockedSelf"
    ? t("limsSstSupervisorGhostFourEyes")
    : t("limsSstSupervisorGhostCapability")
}

// One read-only judged parameter row (the computed RSDs). Entered parameters render inline above.
function ParamRow({ label, value, criterion, pass, t }) {
  return (
    <tr>
      <td>{label}</td>
      <td>{value}</td>
      <td>{criterion}</td>
      <td><ResultFlag pass={pass} t={t} /></td>
    </tr>
  )
}

// #4174 — which template an SST runs against is DERIVED, never chosen at run time: the one frozen
// onto a saved result, otherwise the definition's current default. A saved result therefore keeps
// the template it was judged against even if the definition's default moves on afterwards.
function resolveTemplateId(saved, spec) {
  if (saved && saved.sstTemplateId) return saved.sstTemplateId
  if (spec && spec.defaultSstTemplateId) return spec.defaultSstTemplateId
  return null
}

// The inputs edit TEXT, so a numeric field the record has not got becomes "" rather than "null".
function numText(v) {
  return v === null || v === undefined ? "" : String(v)
}

// The editable draft a saved SST record seeds.
function draftFromSaved(saved) {
  return {
    injections: (saved.injections || []).map((inj) => ({ area: numText(inj.area), rt: numText(inj.rt) })),
    tailing: numText(saved.tailing),
    plateCount: numText(saved.plateCount),
    resolution: numText(saved.resolution),
  }
}

// #4175 — whether the panel renders shut. A complete SST (analyst-signed, and supervisor-signed
// when the definition requires an SST at all) is reference material rather than work, so it starts
// collapsed; anything still in progress stays open. A non-null `override` is the viewer's own click
// and wins from then on.
function isCollapsed(override, { signed, requiresSupervisor, supervisorSigned }) {
  if (override !== null) return override
  if (!signed) return false
  return !requiresSupervisor || supervisorSigned
}

// #4175 — the panel's title row. This is the `action-links` / `action-container` collapsible the
// analysis-task dialog already uses for "Additional information" and "QC results & approvals",
// reused verbatim so the SST folds away the same way as the sections above and below it rather
// than in a style of its own. A finished SST is reference material rather than work, so the panel
// starts shut once it is signed off.
function SstPanelHeader({ collapsed, onToggle, t }) {
  return (
    <div className="action-links" onClick={onToggle}>
      <div className="action-container">
        <span>{t("limsSstPanelTitle")}</span>
        {collapsed ? <IoIosArrowDown /> : <IoIosArrowUp />}
      </div>
    </div>
  )
}

// Pass / Fail / "—" (not judged) badge. `pass` is true | false | null.
function ResultFlag({ pass, t }) {
  if (pass === null || pass === undefined) {
    return <span className="SstPanel-flag is-pending">—</span>
  }
  return (
    <span className={"SstPanel-flag " + (pass ? "is-pass" : "is-fail")}>
      {pass ? t("limsSstPassed") : t("limsSstFailed")}
    </span>
  )
}

// Human-readable criterion strings (null bound → "—", i.e. that criterion is not judged).
function bound(v) {
  return v === null || v === undefined || v === "" ? "—" : String(v)
}
function maxCriterion(max) {
  return max === null || max === undefined ? "—" : `≤ ${bound(max)}`
}
function minCriterion(min) {
  return min === null || min === undefined ? "—" : `≥ ${bound(min)}`
}
function rangeCriterion(min, max) {
  const hasMin = min !== null && min !== undefined
  const hasMax = max !== null && max !== undefined
  if (hasMin && hasMax) return `${bound(min)} – ${bound(max)}`
  if (hasMin) return `≥ ${bound(min)}`
  if (hasMax) return `≤ ${bound(max)}`
  return "—"
}
